import React, { useState, useEffect } from 'react';
import { 
    Building2, Users, Settings, Shield, ChevronRight, 
    Plus, Edit2, Trash2, Eye, X, Loader2, 
    Globe, Database, Activity, AlertTriangle,
    LogIn, BarChart3, Megaphone, Mail, Lock, Calculator, FlaskConical
} from 'lucide-react';
import { apiFetch, setAccessToken } from '../utils/api';
import ScoringStrategyManager from './ScoringStrategyManager';
import SuperAdminHealthTab from './SuperAdminHealthTab';
import SuperAdminAdsTab from './SuperAdminAdsTab';
import SuperAdminSettingsAudit from './SuperAdminSettingsAudit';
import { useI18n } from '../i18n';
import { interpolate } from '../locales/superAdmin';

const DATE_LOCALES = { fi: 'fi-FI', en: 'en-GB', sv: 'sv-SE' };

/**
 * SuperAdminDashboard - Multi-tenant management for SuperAdmins
 * Uses apiFetch to include Authorization Bearer token
 * Validates session on load and requires re-auth if expired
 */
const SuperAdminDashboard = ({ user, onLoginAsClub }) => {
    const { t, language } = useI18n(user?.club?.language);
    const sa = t.superAdmin;
    const dateLocale = DATE_LOCALES[language] || 'fi-FI';
    const [clubs, setClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('clubs');
    const [selectedClub, setSelectedClub] = useState(null);
    const [showClubForm, setShowClubForm] = useState(false);
    const [systemStats, setSystemStats] = useState(null);
    const [scoringClub, setScoringClub] = useState(null); // For club-specific scoring view
    
    // Session validation state
    const [needsReauth, setNeedsReauth] = useState(false);
    const [reauthEmail, setReauthEmail] = useState(user?.email || '');
    const [reauthCode, setReauthCode] = useState('');
    const [reauthStep, setReauthStep] = useState('email'); // 'email' | 'code'
    const [reauthLoading, setReauthLoading] = useState(false);
    const [reauthError, setReauthError] = useState('');

    useEffect(() => {
        validateAndFetch();
    }, []);
    
    // Validate session and fetch data
    const validateAndFetch = async () => {
        setLoading(true);
        try {
            // Try to fetch clubs - this will fail with 401 if session is invalid
            const res = await apiFetch('/api/superadmin/clubs');
            if (res.status === 401 || res.status === 403) {
                console.log('[SuperAdmin] Session invalid, requiring re-authentication');
                setNeedsReauth(true);
                setLoading(false);
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setClubs(data);
            }
            // Also fetch stats
            await fetchSystemStats();
        } catch (err) {
            console.error('Session validation error:', err);
            setNeedsReauth(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchClubs = async () => {
        try {
            const res = await apiFetch('/api/superadmin/clubs');
            if (res.status === 401 || res.status === 403) {
                setNeedsReauth(true);
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setClubs(data);
            } else {
                console.error('Clubs fetch failed:', res.status);
            }
        } catch (err) {
            console.error('Clubs fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSystemStats = async () => {
        try {
            const res = await apiFetch('/api/superadmin/stats');
            if (res.status === 401 || res.status === 403) {
                setNeedsReauth(true);
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setSystemStats(data);
            } else {
                console.error('Stats fetch failed:', res.status);
            }
        } catch (err) {
            console.error('Stats fetch error:', err);
        }
    };
    
    // Re-authentication handlers
    const handleRequestCode = async () => {
        setReauthLoading(true);
        setReauthError('');
        try {
            const res = await fetch('/api/superadmin/auth/request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: reauthEmail })
            });
            if (res.ok) {
                setReauthStep('code');
            } else {
                setReauthError(sa.auth.codeSendError);
            }
        } catch (err) {
            setReauthError(sa.common.networkError);
        } finally {
            setReauthLoading(false);
        }
    };
    
    const handleVerifyCode = async () => {
        setReauthLoading(true);
        setReauthError('');
        try {
            const res = await fetch('/api/superadmin/auth/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: reauthEmail, confirmationKey: reauthCode })
            });
            if (res.ok) {
                const data = await res.json();
                // Store the new access token
                if (data.accessToken) {
                    setAccessToken(data.accessToken);
                }
                // Reset reauth state and reload data
                setNeedsReauth(false);
                setReauthStep('email');
                setReauthCode('');
                setReauthError('');
                // Reload dashboard data
                validateAndFetch();
            } else {
                const errorData = await res.json();
                setReauthError(errorData.error || sa.auth.invalidCode);
            }
        } catch (err) {
            setReauthError(sa.common.networkError);
        } finally {
            setReauthLoading(false);
        }
    };

    const createClub = async (clubData) => {
        try {
            const res = await apiFetch('/api/superadmin/clubs', {
                method: 'POST',
                body: JSON.stringify(clubData)
            });
            if (res.ok) {
                fetchClubs();
                setShowClubForm(false);
            }
        } catch (err) {
            console.error('Club create error:', err);
        }
    };

    const toggleClubStatus = async (clubId, isActive) => {
        try {
            await apiFetch(`/api/superadmin/clubs/${clubId}/status`, {
                method: 'PUT',
                body: JSON.stringify({ isActive: !isActive })
            });
            fetchClubs();
        } catch (err) {
            console.error('Club status error:', err);
        }
    };

    if (loading) {
        return (
            <div className="superadmin-loading">
                <Loader2 size={32} className="spin" />
                <span>{sa.loading}</span>
            </div>
        );
    }
    
    // Re-authentication required
    if (needsReauth) {
        return (
            <div className="superadmin-reauth">
                <div className="reauth-card">
                    <div className="reauth-header">
                        <Lock size={32} />
                        <h2>{sa.auth.sessionExpired}</h2>
                        <p>{sa.auth.sessionExpiredDescription}</p>
                    </div>
                    
                    {reauthStep === 'email' ? (
                        <div className="reauth-form">
                            <label>{sa.auth.emailAddress}</label>
                            <input 
                                type="email" 
                                value={reauthEmail}
                                onChange={(e) => setReauthEmail(e.target.value)}
                                placeholder={sa.auth.emailPlaceholder}
                            />
                            <button 
                                onClick={handleRequestCode} 
                                disabled={reauthLoading || !reauthEmail}
                                className="btn-primary"
                            >
                                {reauthLoading ? (
                                    <><Loader2 size={16} className="spin" /> {sa.common.sending}</>
                                ) : (
                                    <><Mail size={16} /> {sa.auth.sendVerificationCode}</>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="reauth-form">
                            <label>{sa.auth.codeFromEmail}</label>
                            <input 
                                type="text" 
                                value={reauthCode}
                                onChange={(e) => setReauthCode(e.target.value)}
                                placeholder={sa.auth.codePlaceholder}
                                maxLength={6}
                            />
                            <button 
                                onClick={handleVerifyCode} 
                                disabled={reauthLoading || reauthCode.length < 6}
                                className="btn-primary"
                            >
                                {reauthLoading ? (
                                    <><Loader2 size={16} className="spin" /> {sa.common.verifying}</>
                                ) : (
                                    <><Shield size={16} /> {sa.common.verify}</>
                                )}
                            </button>
                            <button 
                                onClick={() => { setReauthStep('email'); setReauthCode(''); }}
                                className="btn-secondary"
                            >
                                {sa.auth.resendCode}
                            </button>
                        </div>
                    )}
                    
                    {reauthError && (
                        <div className="reauth-error">
                            <AlertTriangle size={16} /> {reauthError}
                        </div>
                    )}
                </div>
                
                <style>{`
                    .superadmin-reauth {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        min-height: 80vh;
                        padding: 2rem;
                    }
                    
                    .reauth-card {
                        background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
                        border: 1px solid #374151;
                        border-radius: 16px;
                        padding: 2rem;
                        max-width: 400px;
                        width: 100%;
                    }
                    
                    .reauth-header {
                        text-align: center;
                        margin-bottom: 1.5rem;
                    }
                    
                    .reauth-header svg {
                        color: #f59e0b;
                        margin-bottom: 1rem;
                    }
                    
                    .reauth-header h2 {
                        color: #f0f6fc;
                        margin: 0 0 0.5rem;
                    }
                    
                    .reauth-header p {
                        color: #8b949e;
                        font-size: 0.9rem;
                        margin: 0;
                    }
                    
                    .reauth-form {
                        display: flex;
                        flex-direction: column;
                        gap: 1rem;
                    }
                    
                    .reauth-form label {
                        color: #8b949e;
                        font-size: 0.85rem;
                    }
                    
                    .reauth-form input {
                        padding: 0.75rem;
                        background: rgba(0, 0, 0, 0.3);
                        border: 1px solid #374151;
                        border-radius: 8px;
                        color: #f0f6fc;
                        font-size: 1rem;
                    }
                    
                    .reauth-form input:focus {
                        outline: none;
                        border-color: #8b5cf6;
                    }
                    
                    .reauth-form .btn-primary {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                        padding: 0.75rem;
                        background: #8b5cf6;
                        border: none;
                        border-radius: 8px;
                        color: white;
                        font-weight: 600;
                        cursor: pointer;
                        transition: background 0.2s;
                    }
                    
                    .reauth-form .btn-primary:hover:not(:disabled) {
                        background: #7c3aed;
                    }
                    
                    .reauth-form .btn-primary:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    
                    .reauth-form .btn-secondary {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                        padding: 0.5rem;
                        background: transparent;
                        border: 1px solid #374151;
                        border-radius: 8px;
                        color: #8b949e;
                        cursor: pointer;
                        font-size: 0.85rem;
                    }
                    
                    .reauth-form .btn-secondary:hover {
                        border-color: #8b5cf6;
                        color: #f0f6fc;
                    }
                    
                    .reauth-error {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        margin-top: 1rem;
                        padding: 0.75rem;
                        background: rgba(239, 68, 68, 0.1);
                        border: 1px solid rgba(239, 68, 68, 0.3);
                        border-radius: 8px;
                        color: #f87171;
                        font-size: 0.9rem;
                    }

                    @media (max-width: 600px) {
                        .superadmin-reauth {
                            padding: 1rem;
                            align-items: flex-start;
                        }

                        .reauth-card {
                            padding: 1.5rem 1.25rem;
                        }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="superadmin-dashboard">
            {/* Header */}
            <div className="superadmin-header">
                <div className="superadmin-title">
                    <Shield size={28} />
                    <div>
                        <h1>{sa.title}</h1>
                        <span>{sa.subtitle}</span>
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            {systemStats && (
                <div className="stats-grid">
                    <StatCard 
                        icon={Building2} 
                        label={sa.stats.clubs}
                        value={systemStats.totalClubs} 
                        color="#8b5cf6"
                    />
                    <StatCard 
                        icon={Users} 
                        label={sa.stats.users}
                        value={systemStats.totalUsers}
                        subtitle={interpolate(sa.stats.usersSubtitle, {
                            active: systemStats.activeUsers,
                            inactive: systemStats.inactiveUsers
                        })}
                        color="#22c55e"
                    />
                    <StatCard 
                        icon={BarChart3} 
                        label={sa.stats.sessions}
                        value={systemStats.totalSessions}
                        subtitle={systemStats.sessionsToday > 0
                            ? interpolate(sa.stats.sessionsToday, { count: systemStats.sessionsToday })
                            : null}
                        color="#f59e0b"
                    />
                    <StatCard 
                        icon={Activity} 
                        label={sa.stats.activeToday}
                        value={systemStats.activeToday}
                        subtitle={sa.stats.activeTodaySubtitle}
                        color="#3b82f6"
                    />
                </div>
            )}

            {/* Tabs */}
            <div className="superadmin-tabs">
                <button 
                    className={`tab ${activeTab === 'clubs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('clubs')}
                >
                    <Building2 size={16} /> {sa.tabs.clubs}
                </button>
                <button 
                    className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settings')}
                >
                    <Settings size={16} /> {sa.tabs.settings}
                </button>
                <button 
                    className={`tab ${activeTab === 'ads' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ads')}
                >
                    <Megaphone size={16} /> {sa.tabs.ads}
                </button>
                <button 
                    className={`tab ${activeTab === 'scoring' ? 'active' : ''}`}
                    onClick={() => setActiveTab('scoring')}
                >
                    <Calculator size={16} /> {sa.tabs.scoring}
                </button>
                <button 
                    className={`tab ${activeTab === 'health' ? 'active' : ''}`}
                    onClick={() => setActiveTab('health')}
                >
                    <Activity size={16} /> {sa.tabs.health}
                </button>
            </div>

            {/* Content */}
            <div className="superadmin-content">
                {activeTab === 'clubs' && (
                    <ClubsTab 
                        sa={sa}
                        dateLocale={dateLocale}
                        clubs={clubs}
                        onCreateClick={() => setShowClubForm(true)}
                        onViewClick={setSelectedClub}
                        onToggleStatus={toggleClubStatus}
                        onLoginAsClub={onLoginAsClub}
                        onScoringClick={(club) => {
                            setScoringClub(club);
                            setActiveTab('scoring');
                        }}
                    />
                )}
                
                {activeTab === 'settings' && (
                    <SystemSettingsTab sa={sa} />
                )}
                
                {activeTab === 'ads' && (
                    <SuperAdminAdsTab />
                )}
                
                {activeTab === 'health' && (
                    <SuperAdminHealthTab />
                )}
                
                {activeTab === 'scoring' && (
                    <div>
                        {scoringClub && (
                            <div style={{ 
                                marginBottom: '1rem', 
                                padding: '1rem', 
                                background: 'rgba(139, 92, 246, 0.1)',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '0.75rem'
                            }}>
                                <div>
                                    <span style={{ color: '#8b949e' }}>{sa.scoring.editingClub} </span>
                                    <strong style={{ color: '#a78bfa' }}>{scoringClub.name}</strong>
                                    {sa.scoring.editingSuffix && (
                                        <span style={{ color: '#8b949e' }}> {sa.scoring.editingSuffix}</span>
                                    )}
                                </div>
                                <button 
                                    className="btn-secondary"
                                    onClick={() => setScoringClub(null)}
                                    style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                                >
                                    {sa.scoring.backToGlobal}
                                </button>
                            </div>
                        )}
                        <ScoringStrategyManager 
                            title={scoringClub
                                ? interpolate(sa.scoring.clubSettingsTitle, { name: scoringClub.name })
                                : sa.scoring.globalTemplatesTitle}
                            clubId={scoringClub?.id || null}
                        />
                    </div>
                )}
            </div>

            {/* Club Details Modal */}
            {selectedClub && (
                <ClubDetailsModal 
                    sa={sa}
                    dateLocale={dateLocale}
                    club={selectedClub}
                    onClose={() => setSelectedClub(null)}
                    onRefresh={fetchClubs}
                />
            )}

            {/* Create Club Modal */}
            {showClubForm && (
                <CreateClubModal 
                    sa={sa}
                    onClose={() => setShowClubForm(false)}
                    onCreate={createClub}
                />
            )}

            <style>{`
                .superadmin-dashboard {
                    padding: 1.5rem;
                    max-width: 1400px;
                    margin: 0 auto;
                    min-height: 100vh;
                    overflow-x: hidden;
                }

                .superadmin-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 1rem;
                    padding: 3rem;
                    color: #8b949e;
                }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .superadmin-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #30363d;
                }

                .superadmin-title {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    color: #f0f6fc;
                }

                .superadmin-title h1 {
                    margin: 0;
                    font-size: 1.5rem;
                }

                .superadmin-title span {
                    color: #8b949e;
                    font-size: 0.85rem;
                }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                    margin-bottom: 2rem;
                }

                .stat-card {
                    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
                    border: 1px solid #374151;
                    border-radius: 12px;
                    padding: 1.25rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .stat-card-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .stat-card-value {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: #f0f6fc;
                }

                .stat-card-label {
                    font-size: 0.85rem;
                    color: #8b949e;
                }

                .stat-card-subtitle {
                    font-size: 0.75rem;
                    color: #6b7280;
                    margin-top: 0.25rem;
                }

                .superadmin-tabs {
                    display: flex;
                    gap: 0.5rem;
                    margin-bottom: 1.5rem;
                    border-bottom: 1px solid #30363d;
                    padding-bottom: 0.5rem;
                }

                .tab {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.25rem;
                    background: transparent;
                    border: none;
                    color: #8b949e;
                    cursor: pointer;
                    border-radius: 8px 8px 0 0;
                    transition: all 0.2s;
                }

                .tab:hover {
                    background: rgba(255, 255, 255, 0.05);
                    color: #f0f6fc;
                }

                .tab.active {
                    background: rgba(139, 92, 246, 0.1);
                    color: #a78bfa;
                    border-bottom: 2px solid #8b5cf6;
                }

                .superadmin-content {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 12px;
                    padding: 1.5rem;
                }

                /* Clubs Table */
                .clubs-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }

                .clubs-header h3 {
                    margin: 0;
                    color: #f0f6fc;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .clubs-table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: auto;
                }

                .clubs-table th,
                .clubs-table td {
                    padding: 0.75rem;
                    text-align: left;
                    border-bottom: 1px solid #30363d;
                }

                .clubs-table th {
                    color: #8b949e;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                    font-weight: 600;
                }

                .clubs-table td {
                    color: #c9d1d9;
                }

                .clubs-table tr:hover td {
                    background: rgba(255, 255, 255, 0.02);
                }

                .club-name {
                    font-weight: 600;
                    color: #f0f6fc;
                }

                .club-slug {
                    font-size: 0.8rem;
                    color: #8b949e;
                }

                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.5rem;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .status-badge.active {
                    background: rgba(34, 197, 94, 0.15);
                    color: #22c55e;
                }

                .status-badge.inactive {
                    background: rgba(239, 68, 68, 0.15);
                    color: #ef4444;
                }

                .action-btns {
                    display: flex;
                    gap: 0.5rem;
                }

                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    border: 1px solid #374151;
                    background: transparent;
                    color: #8b949e;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }

                .action-btn:hover {
                    background: rgba(255, 255, 255, 0.1);
                    color: #f0f6fc;
                }

                .action-btn.primary:hover {
                    background: rgba(139, 92, 246, 0.2);
                    border-color: #8b5cf6;
                    color: #a78bfa;
                }

                .btn-primary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.6rem 1rem;
                    background: #8b5cf6;
                    border: none;
                    border-radius: 8px;
                    color: #fff;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .btn-primary:hover {
                    background: #7c3aed;
                }

                /* Modal */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                    padding: 1rem;
                }

                .modal-content {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    max-width: 600px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.25rem;
                    border-bottom: 1px solid #30363d;
                }

                .modal-header h3 {
                    margin: 0;
                    color: #f0f6fc;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .modal-close {
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    color: #9ca3af;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close:hover {
                    background: rgba(255, 255, 255, 0.2);
                    color: #fff;
                }

                .modal-body {
                    padding: 1.25rem;
                }

                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    padding: 1.25rem;
                    border-top: 1px solid #30363d;
                }

                .form-group {
                    margin-bottom: 1rem;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 0.5rem;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                    width: 100%;
                    padding: 0.75rem;
                    background: #0d1117;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    font-size: 0.9rem;
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #8b5cf6;
                }

                .btn-secondary {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.6rem 1rem;
                    background: #21262d;
                    border: 1px solid #30363d;
                    border-radius: 8px;
                    color: #c9d1d9;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-secondary:hover {
                    background: #30363d;
                }

                @media (max-width: 768px) {
                    .superadmin-dashboard {
                        padding: 1rem;
                    }

                    .stats-grid {
                        grid-template-columns: 1fr 1fr;
                    }

                    .superadmin-tabs {
                        flex-wrap: wrap;
                    }

                    .clubs-table {
                        font-size: 0.85rem;
                    }

                    .clubs-table th:nth-child(3),
                    .clubs-table td:nth-child(3),
                    .clubs-table th:nth-child(4),
                    .clubs-table td:nth-child(4) {
                        display: none;
                    }
                }

                /* Portrait phones: stack the club table into cards so nothing is cut off */
                @media (max-width: 600px) {
                    .superadmin-dashboard {
                        padding: 0.75rem;
                    }

                    .superadmin-header {
                        margin-bottom: 1.25rem;
                    }

                    .superadmin-title h1 {
                        font-size: 1.2rem;
                    }

                    .stats-grid {
                        grid-template-columns: 1fr;
                        gap: 0.75rem;
                        margin-bottom: 1.25rem;
                    }

                    .stat-card {
                        padding: 1rem;
                    }

                    .superadmin-tabs {
                        flex-wrap: nowrap;
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        scrollbar-width: none;
                    }

                    .superadmin-tabs::-webkit-scrollbar {
                        display: none;
                    }

                    .tab {
                        flex: 0 0 auto;
                        padding: 0.6rem 0.85rem;
                        font-size: 0.85rem;
                    }

                    .superadmin-content {
                        padding: 1rem;
                    }

                    .clubs-header {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 0.75rem;
                    }

                    .clubs-header .btn-primary {
                        justify-content: center;
                    }

                    .clubs-table,
                    .clubs-table tbody,
                    .clubs-table tr,
                    .clubs-table td {
                        display: block;
                        width: 100%;
                    }

                    .clubs-table thead {
                        display: none;
                    }

                    .clubs-table tr {
                        border: 1px solid #30363d;
                        border-radius: 10px;
                        margin-bottom: 0.75rem;
                        padding: 0.25rem 0.75rem;
                        background: rgba(255, 255, 255, 0.02);
                    }

                    .clubs-table td {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 1rem;
                        padding: 0.5rem 0;
                        border-bottom: 1px solid rgba(48, 54, 61, 0.6);
                    }

                    .clubs-table tr td:last-child {
                        border-bottom: none;
                    }

                    .clubs-table td::before {
                        content: attr(data-label);
                        color: #8b949e;
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        font-weight: 600;
                        flex: 0 0 auto;
                    }

                    .clubs-table td:first-child {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.15rem;
                    }

                    .clubs-table th:nth-child(3),
                    .clubs-table td:nth-child(3),
                    .clubs-table th:nth-child(4),
                    .clubs-table td:nth-child(4) {
                        display: flex;
                    }

                    .action-btns {
                        flex-wrap: wrap;
                        justify-content: flex-end;
                    }

                    .modal-overlay {
                        padding: 0.5rem;
                        align-items: flex-start;
                    }

                    .modal-content {
                        max-height: calc(100vh - 1rem);
                    }

                    .modal-header,
                    .modal-body,
                    .modal-footer {
                        padding: 1rem;
                    }

                    .modal-footer {
                        flex-direction: column-reverse;
                    }

                    .modal-footer .btn-primary,
                    .modal-footer .btn-secondary {
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtitle, color }) => (
    <div className="stat-card">
        <div className="stat-card-icon" style={{ background: `${color}20` }}>
            <Icon size={24} style={{ color }} />
        </div>
        <div>
            <div className="stat-card-value">{value}</div>
            <div className="stat-card-label">{label}</div>
            {subtitle && <div className="stat-card-subtitle">{subtitle}</div>}
        </div>
    </div>
);

// Clubs Tab with search
const ClubsTab = ({ sa, dateLocale, clubs, onCreateClick, onViewClick, onToggleStatus, onLoginAsClub, onScoringClick }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [displayLimit, setDisplayLimit] = useState(50);
    
    const filteredClubs = clubs.filter(club => 
        club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        club.slug.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const displayedClubs = filteredClubs.slice(0, displayLimit);
    const hasMore = filteredClubs.length > displayLimit;
    
    return (
        <div>
            <div className="clubs-header">
                <h3><Building2 size={20} /> {interpolate(sa.clubs.registered, { count: clubs.length })}</h3>
                <button className="btn-primary" onClick={onCreateClick}>
                    <Plus size={16} /> {sa.clubs.createNew}
                </button>
            </div>
            
            {/* Search */}
            <div style={{ marginBottom: '1rem' }}>
                <input 
                    type="text"
                    placeholder={sa.clubs.searchPlaceholder}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '0.75rem 1rem',
                        background: '#0d1117',
                        border: '1px solid #30363d',
                        borderRadius: '8px',
                        color: '#c9d1d9',
                        fontSize: '0.9rem'
                    }}
                />
                {searchTerm && (
                    <span style={{ marginLeft: '1rem', color: '#8b949e' }}>
                        {interpolate(sa.clubs.searchResults, { count: filteredClubs.length })}
                    </span>
                )}
            </div>
            
            <table className="clubs-table">
                <thead>
                    <tr>
                        <th>{sa.common.club}</th>
                        <th>{sa.common.members}</th>
                        <th>{sa.common.games}</th>
                        <th>{sa.common.created}</th>
                        <th>{sa.common.status}</th>
                        <th>{sa.common.actions}</th>
                    </tr>
                </thead>
                <tbody>
                    {displayedClubs.map(club => (
                        <tr key={club.id}>
                            <td data-label={sa.common.club}>
                                <div className="club-name">{club.name}</div>
                                <div className="club-slug">/{club.slug}</div>
                            </td>
                            <td data-label={sa.common.members}>{club.memberCount || 0}</td>
                            <td data-label={sa.common.games}>{club.gameCount || 0}</td>
                            <td data-label={sa.common.created}>{new Date(club.created_at).toLocaleDateString(dateLocale)}</td>
                            <td data-label={sa.common.status}>
                                <span className={`status-badge ${club.is_active ? 'active' : 'inactive'}`}>
                                    {club.is_active ? sa.common.active : sa.common.inactive}
                                </span>
                            </td>
                            <td data-label={sa.common.actions}>
                                <div className="action-btns">
                                    <button 
                                        className="action-btn" 
                                        title={sa.clubs.viewDetails}
                                        onClick={() => onViewClick(club)}
                                    >
                                        <Eye size={16} />
                                    </button>
                                    <button 
                                        className="action-btn" 
                                        title={sa.clubs.scoring}
                                        onClick={() => onScoringClick && onScoringClick(club)}
                                    >
                                        <Calculator size={16} />
                                    </button>
                                    <button 
                                        className="action-btn primary" 
                                        title={sa.clubs.loginAsAdmin}
                                        onClick={() => onLoginAsClub && onLoginAsClub(club.id)}
                                    >
                                        <LogIn size={16} />
                                    </button>
                                    <button 
                                        className="action-btn" 
                                        title={club.is_active ? sa.common.deactivate : sa.common.activate}
                                        onClick={() => onToggleStatus(club.id, club.is_active)}
                                    >
                                        {club.is_active ? <AlertTriangle size={16} /> : <Activity size={16} />}
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            
            {hasMore && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <button 
                        className="btn-secondary"
                        onClick={() => setDisplayLimit(prev => prev + 50)}
                    >
                        {interpolate(sa.clubs.showMore, { remaining: filteredClubs.length - displayLimit })}
                    </button>
                </div>
            )}
        </div>
    );
};

// System Settings Tab
const SystemSettingsTab = ({ sa }) => {
    const [activeSection, setActiveSection] = useState('smtp');
    
    const tabStyle = (section) => ({
        padding: '0.5rem 1rem',
        background: activeSection === section ? 'rgba(139, 92, 246, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        border: `1px solid ${activeSection === section ? '#8b5cf6' : '#374151'}`,
        borderRadius: '6px',
        color: activeSection === section ? '#a78bfa' : '#8b949e',
        cursor: 'pointer'
    });
    
    return (
        <div>
            <h3 style={{ color: '#f0f6fc', marginBottom: '1rem' }}>
                <Settings size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                {sa.settings.title}
            </h3>
            <p style={{ color: '#8b949e', marginBottom: '1.5rem' }}>
                {sa.settings.description}
            </p>
            
            {/* Settings Sections */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <button onClick={() => setActiveSection('smtp')} style={tabStyle('smtp')}>
                    📧 {sa.settings.sections.smtp}
                </button>
                <button onClick={() => setActiveSection('ai')} style={tabStyle('ai')}>
                    🤖 {sa.settings.sections.ai}
                </button>
                <button onClick={() => setActiveSection('prompts')} style={tabStyle('prompts')}>
                    💬 {sa.settings.sections.prompts}
                </button>
                <button onClick={() => setActiveSection('general')} style={tabStyle('general')}>
                    ⚙️ {sa.settings.sections.general}
                </button>
                <button onClick={() => setActiveSection('audit')} style={tabStyle('audit')}>
                    📋 {sa.settings.sections.audit}
                </button>
            </div>
            
            {activeSection === 'smtp' && <SMTPSettingsSection sa={sa} />}
            {activeSection === 'ai' && <AISettingsSection sa={sa} />}
            {activeSection === 'prompts' && <PromptsSettingsSection sa={sa} />}
            {activeSection === 'general' && <GeneralSettingsSection sa={sa} />}
            {activeSection === 'audit' && <SuperAdminSettingsAudit />}
        </div>
    );
};

// SMTP Settings Section
const SMTPSettingsSection = ({ sa }) => {
    const [smtpSettings, setSmtpSettings] = useState({
        smtp_host: '',
        smtp_port: '465',
        smtp_user: '',
        smtp_password: '',
        smtp_secure: 'true',
        smtp_from_name: '',
        smtp_from_email: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchSMTPSettings();
    }, []);

    const fetchSMTPSettings = async () => {
        try {
            const res = await apiFetch('/api/superadmin/settings/smtp');
            if (res.ok) {
                const data = await res.json();
                setSmtpSettings(data);
            }
        } catch (err) {
            console.error('SMTP fetch error:', err);
            setMessage({ type: 'error', text: sa.settings.smtp.fetchError });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage({ type: '', text: '' });
        
        try {
            const res = await apiFetch('/api/superadmin/settings/smtp', {
                method: 'PUT',
                body: JSON.stringify(smtpSettings)
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: `✅ ${sa.settings.smtp.saveSuccess}` });
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || sa.settings.smtp.saveFailed });
            }
        } catch (err) {
            setMessage({ type: 'error', text: sa.common.networkError });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!testEmail) {
            setMessage({ type: 'error', text: sa.settings.smtp.testEmailRequired });
            return;
        }
        
        setTesting(true);
        setMessage({ type: '', text: '' });
        
        try {
            const res = await apiFetch('/api/superadmin/settings/smtp/test', {
                method: 'POST',
                body: JSON.stringify({ email: testEmail })
            });
            
            const data = await res.json();
            
            if (res.ok) {
                setMessage({ type: 'success', text: `✅ ${interpolate(sa.settings.smtp.testSuccess, { email: testEmail })}` });
            } else {
                setMessage({ type: 'error', text: data.error || sa.settings.smtp.testFailed });
            }
        } catch (err) {
            setMessage({ type: 'error', text: sa.common.networkError });
        } finally {
            setTesting(false);
        }
    };

    const handleChange = (field, value) => {
        setSmtpSettings(prev => ({ ...prev, [field]: value }));
    };

    if (loading) return <p style={{ color: '#8b949e' }}>{sa.settings.smtp.loading}</p>;

    const inputStyle = {
        width: '100%',
        padding: '0.75rem',
        background: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid #374151',
        borderRadius: '6px',
        color: '#f0f6fc',
        fontSize: '0.95rem'
    };

    const labelStyle = {
        display: 'block',
        color: '#8b949e',
        marginBottom: '0.5rem',
        fontSize: '0.85rem'
    };

    return (
        <div style={{ maxWidth: '600px' }}>
            <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #374151'
            }}>
                <h4 style={{ color: '#f0f6fc', marginTop: 0, marginBottom: '1rem' }}>
                    📧 {sa.settings.smtp.title}
                </h4>
                <p style={{ color: '#8b949e', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                    {sa.settings.smtp.description}
                </p>

                <div style={{ display: 'grid', gap: '1rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                        <div>
                            <label style={labelStyle}>{sa.settings.smtp.host}</label>
                            <input
                                type="text"
                                value={smtpSettings.smtp_host}
                                onChange={(e) => handleChange('smtp_host', e.target.value)}
                                placeholder={sa.settings.smtp.hostPlaceholder}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>{sa.settings.smtp.port}</label>
                            <input
                                type="text"
                                value={smtpSettings.smtp_port}
                                onChange={(e) => handleChange('smtp_port', e.target.value)}
                                placeholder={sa.settings.smtp.portPlaceholder}
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label style={labelStyle}>{sa.settings.smtp.username}</label>
                            <input
                                type="text"
                                value={smtpSettings.smtp_user}
                                onChange={(e) => handleChange('smtp_user', e.target.value)}
                                placeholder={sa.settings.smtp.usernamePlaceholder}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>{sa.settings.smtp.password}</label>
                            <input
                                type="password"
                                value={smtpSettings.smtp_password}
                                onChange={(e) => handleChange('smtp_password', e.target.value)}
                                placeholder={sa.settings.smtp.passwordPlaceholder}
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    <div>
                        <label style={labelStyle}>{sa.settings.smtp.sslTls}</label>
                        <select
                            value={smtpSettings.smtp_secure}
                            onChange={(e) => handleChange('smtp_secure', e.target.value)}
                            style={inputStyle}
                        >
                            <option value="true">{sa.settings.smtp.sslOption}</option>
                            <option value="false">{sa.settings.smtp.startTlsOption}</option>
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label style={labelStyle}>{sa.settings.smtp.fromName}</label>
                            <input
                                type="text"
                                value={smtpSettings.smtp_from_name}
                                onChange={(e) => handleChange('smtp_from_name', e.target.value)}
                                placeholder={sa.settings.smtp.fromNamePlaceholder}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>{sa.settings.smtp.fromEmail}</label>
                            <input
                                type="email"
                                value={smtpSettings.smtp_from_email}
                                onChange={(e) => handleChange('smtp_from_email', e.target.value)}
                                placeholder={sa.settings.smtp.fromEmailPlaceholder}
                                style={inputStyle}
                            />
                        </div>
                    </div>
                </div>

                {/* Message */}
                {message.text && (
                    <div style={{
                        marginTop: '1rem',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        background: message.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        border: `1px solid ${message.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                        color: message.type === 'success' ? '#22c55e' : '#ef4444'
                    }}>
                        {message.text}
                    </div>
                )}

                {/* Save Button */}
                <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                        marginTop: '1.5rem',
                        width: '100%',
                        padding: '0.75rem',
                        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontWeight: '500',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        opacity: saving ? 0.7 : 1
                    }}
                >
                    {saving ? sa.common.saving : sa.settings.smtp.saveSettings}
                </button>

                {/* Test Section */}
                <div style={{
                    marginTop: '1.5rem',
                    paddingTop: '1.5rem',
                    borderTop: '1px solid #374151'
                }}>
                    <h5 style={{ color: '#f0f6fc', marginTop: 0, marginBottom: '0.75rem' }}>
                        🧪 {sa.settings.smtp.testTitle}
                    </h5>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder={sa.settings.smtp.testEmailPlaceholder}
                            style={{ ...inputStyle, flex: 1 }}
                        />
                        <button
                            onClick={handleTest}
                            disabled={testing}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'rgba(34, 197, 94, 0.2)',
                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                borderRadius: '6px',
                                color: '#22c55e',
                                cursor: testing ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {testing ? sa.common.testing : sa.settings.smtp.sendTest}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// AI Settings Section
const AISettingsSection = ({ sa }) => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [editValues, setEditValues] = useState({});
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);
    const [revealedKeys, setRevealedKeys] = useState({});

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await apiFetch('/api/admin/settings');
                if (res.ok) {
                    const data = await res.json();
                    const aiSettings = data.filter(s => s.category === 'ai');
                    setSettings(aiSettings);
                    const values = {};
                    aiSettings.forEach(s => { values[s.setting_key] = s.setting_value; });
                    setEditValues(values);
                }
            } catch (err) {
                console.error('AI settings fetch error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSave = async (key) => {
        setSaving(prev => ({ ...prev, [key]: true }));
        try {
            const res = await apiFetch(`/api/admin/settings/${key}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: editValues[key] })
            });
            if (res.ok) {
                setSaving(prev => ({ ...prev, [key]: 'success' }));
                setTimeout(() => setSaving(prev => ({ ...prev, [key]: false })), 1500);
            }
        } catch (err) {
            console.error('Save error:', err);
            setSaving(prev => ({ ...prev, [key]: 'error' }));
        }
    };

    const handleReveal = async (key) => {
        if (revealedKeys[key]) {
            setRevealedKeys(prev => ({ ...prev, [key]: false }));
            return;
        }
        try {
            const res = await apiFetch(`/api/admin/settings/${key}?reveal=true`);
            const data = await res.json();
            setEditValues(prev => ({ ...prev, [key]: data.setting_value }));
            setRevealedKeys(prev => ({ ...prev, [key]: true }));
        } catch (err) {
            console.error('Reveal error:', err);
        }
    };

    const testAI = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await apiFetch('/api/admin/ai-test');
            const data = await res.json();
            setTestResult(data);
        } catch (err) {
            setTestResult({ success: false, error: err.message });
        } finally {
            setTesting(false);
        }
    };

    if (loading) return <p style={{ color: '#8b949e' }}>{sa.settings.ai.loading}</p>;

    return (
        <div>
            {/* AI Test */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid #374151' }}>
                <h4 style={{ color: '#f0f6fc', margin: '0 0 1rem' }}>🧪 {sa.settings.ai.testTitle}</h4>
                <button 
                    onClick={testAI} 
                    disabled={testing}
                    style={{
                        padding: '0.5rem 1rem',
                        background: '#8b5cf6',
                        border: 'none',
                        borderRadius: '6px',
                        color: 'white',
                        cursor: testing ? 'wait' : 'pointer'
                    }}
                >
                    {testing ? sa.common.testing : sa.settings.ai.testConnection}
                </button>
                {testResult && (
                    <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: testResult.success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        border: `1px solid ${testResult.success ? '#22c55e' : '#ef4444'}`,
                        borderRadius: '6px',
                        color: testResult.success ? '#22c55e' : '#ef4444'
                    }}>
                        {testResult.success ? (
                            <>
                                <strong>✓ {sa.settings.ai.connectionSuccess}</strong>
                                <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>{sa.settings.ai.model}: {testResult.model}</p>
                            </>
                        ) : (
                            <>
                                <strong>✗ {sa.settings.ai.connectionFailed}</strong>
                                <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>{testResult.error}</p>
                            </>
                        )}
                    </div>
                )}
            </div>
            
            {/* AI Settings List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {settings.map(s => (
                    <div key={s.setting_key} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #374151'
                    }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#8b949e', fontSize: '0.85rem' }}>{s.description || s.setting_key}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {s.is_sensitive ? (
                                <>
                                    <input
                                        type={revealedKeys[s.setting_key] ? 'text' : 'password'}
                                        value={editValues[s.setting_key] || ''}
                                        onChange={e => setEditValues(prev => ({ ...prev, [s.setting_key]: e.target.value }))}
                                        style={{
                                            flex: 1,
                                            padding: '0.5rem',
                                            background: '#0d1117',
                                            border: '1px solid #30363d',
                                            borderRadius: '6px',
                                            color: '#c9d1d9'
                                        }}
                                    />
                                    <button 
                                        onClick={() => handleReveal(s.setting_key)}
                                        style={{ padding: '0.5rem 0.75rem', background: '#21262d', border: '1px solid #30363d', borderRadius: '6px', color: '#8b949e', cursor: 'pointer' }}
                                    >
                                        {revealedKeys[s.setting_key] ? '🙈' : '👁️'}
                                    </button>
                                </>
                            ) : (
                                <input
                                    type="text"
                                    value={editValues[s.setting_key] || ''}
                                    onChange={e => setEditValues(prev => ({ ...prev, [s.setting_key]: e.target.value }))}
                                    style={{
                                        flex: 1,
                                        padding: '0.5rem',
                                        background: '#0d1117',
                                        border: '1px solid #30363d',
                                        borderRadius: '6px',
                                        color: '#c9d1d9'
                                    }}
                                />
                            )}
                            <button 
                                onClick={() => handleSave(s.setting_key)}
                                disabled={saving[s.setting_key] === true}
                                style={{ 
                                    padding: '0.5rem 1rem', 
                                    background: saving[s.setting_key] === 'success' ? '#22c55e' : '#238636', 
                                    border: 'none', 
                                    borderRadius: '6px', 
                                    color: 'white', 
                                    cursor: 'pointer' 
                                }}
                            >
                                {saving[s.setting_key] === 'success' ? '✓' : sa.common.save}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            
            {/* AI Help */}
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px' }}>
                <h4 style={{ color: '#60a5fa', margin: '0 0 0.5rem' }}>💡 {sa.settings.ai.tipsTitle}</h4>
                <ul style={{ color: '#8b949e', margin: 0, paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
                    <li><strong>gemini-2.0-flash</strong> – {sa.settings.ai.tipFlash}</li>
                    <li><strong>gemini-pro</strong> – {sa.settings.ai.tipPro}</li>
                    <li><strong>gemini-1.5-pro</strong> – {sa.settings.ai.tipPro15}</li>
                </ul>
            </div>
        </div>
    );
};

// Prompts Settings Section
const PromptsSettingsSection = ({ sa }) => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [editValues, setEditValues] = useState({});

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await apiFetch('/api/admin/settings');
                if (res.ok) {
                    const data = await res.json();
                    const promptSettings = data.filter(s => s.category === 'prompts');
                    setSettings(promptSettings);
                    const values = {};
                    promptSettings.forEach(s => { values[s.setting_key] = s.setting_value; });
                    setEditValues(values);
                }
            } catch (err) {
                console.error('Prompts settings fetch error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSave = async (key) => {
        setSaving(prev => ({ ...prev, [key]: true }));
        try {
            const res = await apiFetch(`/api/admin/settings/${key}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: editValues[key] })
            });
            if (res.ok) {
                setSaving(prev => ({ ...prev, [key]: 'success' }));
                setTimeout(() => setSaving(prev => ({ ...prev, [key]: false })), 1500);
            }
        } catch (err) {
            console.error('Save error:', err);
            setSaving(prev => ({ ...prev, [key]: 'error' }));
        }
    };

    if (loading) return <p style={{ color: '#8b949e' }}>{sa.settings.prompts.loading}</p>;

    return (
        <div>
            <p style={{ color: '#8b949e', marginBottom: '1rem' }}>
                {sa.settings.prompts.description}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {settings.map(s => (
                    <div key={s.setting_key} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #374151'
                    }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                            <span style={{ color: '#f0f6fc', fontWeight: '500' }}>{s.description || s.setting_key}</span>
                        </div>
                        <textarea
                            value={editValues[s.setting_key] || ''}
                            onChange={e => setEditValues(prev => ({ ...prev, [s.setting_key]: e.target.value }))}
                            rows={8}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: '#0d1117',
                                border: '1px solid #30363d',
                                borderRadius: '6px',
                                color: '#c9d1d9',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                resize: 'vertical'
                            }}
                        />
                        <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={() => handleSave(s.setting_key)}
                                disabled={saving[s.setting_key] === true}
                                style={{ 
                                    padding: '0.5rem 1rem', 
                                    background: saving[s.setting_key] === 'success' ? '#22c55e' : '#238636', 
                                    border: 'none', 
                                    borderRadius: '6px', 
                                    color: 'white', 
                                    cursor: 'pointer' 
                                }}
                            >
                                {saving[s.setting_key] === 'success' ? `✓ ${sa.common.saved}` : sa.common.save}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// General Settings Section
const GeneralSettingsSection = ({ sa }) => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await apiFetch('/api/settings');
                if (res.ok) {
                    const data = await res.json();
                    setSettings(data);
                }
            } catch (err) {
                console.error('Settings fetch error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    if (loading) return <p style={{ color: '#8b949e' }}>{sa.settings.general.loading}</p>;

    return (
        <div>
            <p style={{ color: '#8b949e', marginBottom: '1rem' }}>
                {sa.settings.general.description}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {settings.map(s => (
                    <div key={s.key} style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #374151'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <strong style={{ color: '#f0f6fc' }}>{s.key}</strong>
                                <p style={{ color: '#8b949e', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
                                    {s.description || sa.common.noDescription}
                                </p>
                            </div>
                            <span style={{ 
                                color: '#a78bfa',
                                background: 'rgba(139, 92, 246, 0.1)',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.85rem'
                            }}>
                                {s.value}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Club Details Modal (includes per-club beta feature toggles)
const ClubDetailsModal = ({ sa, dateLocale, club, onClose, onRefresh }) => {
    const [catalog, setCatalog] = useState([]);
    const [enabled, setEnabled] = useState({});
    const [loadingFeatures, setLoadingFeatures] = useState(true);
    const [savingFeature, setSavingFeature] = useState(null);
    const [featureError, setFeatureError] = useState('');
    const [planTier, setPlanTier] = useState(club.plan_tier || 'free');
    const [savingPlan, setSavingPlan] = useState(false);
    const [planError, setPlanError] = useState('');

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoadingFeatures(true);
            setFeatureError('');
            try {
                const res = await apiFetch(`/api/superadmin/clubs/${club.id}/beta-features`);
                if (!res.ok) {
                    throw new Error(sa.clubs.betaLoadError);
                }
                const data = await res.json();
                if (!cancelled) {
                    setCatalog(data.catalog || []);
                    setEnabled(data.enabled || {});
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setFeatureError(err.message || sa.common.error);
            } finally {
                if (!cancelled) setLoadingFeatures(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [club.id]);

    const toggleFeature = async (featureId) => {
        const next = !enabled[featureId];
        setSavingFeature(featureId);
        setFeatureError('');
        try {
            const res = await apiFetch(`/api/superadmin/clubs/${club.id}/beta-features`, {
                method: 'PUT',
                body: JSON.stringify({ featureId, enabled: next })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || sa.clubs.saveFailed);
            }
            const data = await res.json();
            setEnabled(data.enabled || {});
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error(err);
            setFeatureError(err.message || sa.clubs.saveFailed);
        } finally {
            setSavingFeature(null);
        }
    };

    const savePlan = async (nextTier) => {
        setSavingPlan(true);
        setPlanError('');
        try {
            const res = await apiFetch(`/api/superadmin/clubs/${club.id}/plan`, {
                method: 'PUT',
                body: JSON.stringify({
                    planTier: nextTier,
                    note: sa.clubs.planGrantedWithoutBilling
                })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || sa.clubs.saveFailed);
            setPlanTier(data.planTier || nextTier);
            if (onRefresh) onRefresh();
        } catch (err) {
            setPlanError(err.message || sa.clubs.saveFailed);
        } finally {
            setSavingPlan(false);
        }
    };

    return (
    <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <h3><Building2 size={20} /> {club.name}</h3>
                <button className="modal-close" onClick={onClose}>
                    <X size={18} />
                </button>
            </div>
            <div className="modal-body">
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>{sa.common.slug}</label>
                        <p style={{ color: '#f0f6fc', margin: '0.25rem 0' }}>/{club.slug}</p>
                    </div>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>{sa.common.description}</label>
                        <p style={{ color: '#f0f6fc', margin: '0.25rem 0' }}>{club.description || '-'}</p>
                    </div>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>{sa.common.language}</label>
                        <p style={{ color: '#f0f6fc', margin: '0.25rem 0' }}>{club.language?.toUpperCase() || 'FI'}</p>
                    </div>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>{sa.common.created}</label>
                        <p style={{ color: '#f0f6fc', margin: '0.25rem 0' }}>
                            {new Date(club.created_at).toLocaleString(dateLocale)}
                        </p>
                    </div>
                    <div>
                        <label style={{ color: '#8b949e', fontSize: '0.8rem' }}>{sa.common.color}</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ 
                                width: '24px', 
                                height: '24px', 
                                borderRadius: '4px',
                                background: club.primary_color || '#8b5cf6'
                            }} />
                            <span style={{ color: '#f0f6fc' }}>{club.primary_color || '#8b5cf6'}</span>
                        </div>
                    </div>

                    <div style={{
                        marginTop: '0.5rem',
                        paddingTop: '1rem',
                        borderTop: '1px solid #30363d'
                    }}>
                        <label style={{ color: '#f0f6fc', fontSize: '0.95rem', fontWeight: 600 }}>
                            {sa.clubs.planTier}
                        </label>
                        <p style={{ color: '#8b949e', fontSize: '0.8rem', margin: '0.35rem 0 0.75rem' }}>
                            {sa.clubs.planDescription}
                        </p>
                        <select
                            value={planTier}
                            disabled={savingPlan}
                            onChange={(event) => savePlan(event.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.6rem',
                                borderRadius: '6px',
                                border: '1px solid #30363d',
                                background: '#0d1117',
                                color: '#f0f6fc'
                            }}
                        >
                            <option value="free">{sa.clubs.planFree}</option>
                            <option value="pro">{sa.clubs.planPro}</option>
                        </select>
                        {savingPlan && <small style={{ color: '#8b949e' }}>{sa.common.saving}</small>}
                        {planError && <div style={{ color: '#f85149', marginTop: '0.5rem' }}>{planError}</div>}
                    </div>

                    <div style={{
                        marginTop: '0.5rem',
                        paddingTop: '1rem',
                        borderTop: '1px solid #30363d'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <FlaskConical size={18} color="#a78bfa" />
                            <label style={{ color: '#f0f6fc', fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>
                                {sa.clubs.betaFeatures}
                            </label>
                        </div>
                        <p style={{ color: '#8b949e', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
                            {sa.clubs.betaFeaturesDescription}
                        </p>
                        {loadingFeatures ? (
                            <div style={{ color: '#8b949e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Loader2 size={16} className="spin" /> {sa.common.loading}
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gap: '0.75rem' }}>
                                {catalog.map((feature) => {
                                    const isOn = !!enabled[feature.id];
                                    const isSaving = savingFeature === feature.id;
                                    return (
                                        <div
                                            key={feature.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: '1rem',
                                                padding: '0.75rem',
                                                background: 'rgba(0,0,0,0.25)',
                                                borderRadius: '8px',
                                                border: '1px solid #30363d'
                                            }}
                                        >
                                            <div>
                                                <div style={{ color: '#f0f6fc', fontWeight: 500 }}>{feature.name}</div>
                                                <div style={{ color: '#8b949e', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                                    {feature.description}
                                                </div>
                                                <code style={{ color: '#6e7681', fontSize: '0.7rem' }}>{feature.id}</code>
                                            </div>
                                            <button
                                                type="button"
                                                disabled={isSaving}
                                                onClick={() => toggleFeature(feature.id)}
                                                title={isOn ? sa.common.deactivate : sa.common.activate}
                                                style={{
                                                    flexShrink: 0,
                                                    minWidth: '72px',
                                                    padding: '0.4rem 0.75rem',
                                                    borderRadius: '6px',
                                                    border: `1px solid ${isOn ? '#238636' : '#30363d'}`,
                                                    background: isOn ? 'rgba(35, 134, 54, 0.25)' : 'rgba(0,0,0,0.3)',
                                                    color: isOn ? '#3fb950' : '#8b949e',
                                                    cursor: isSaving ? 'wait' : 'pointer',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {isSaving ? <Loader2 size={14} className="spin" /> : (isOn ? sa.common.on : sa.common.off)}
                                            </button>
                                        </div>
                                    );
                                })}
                                {catalog.length === 0 && (
                                    <p style={{ color: '#8b949e', margin: 0 }}>{sa.clubs.noBetaFeatures}</p>
                                )}
                            </div>
                        )}
                        {featureError && (
                            <p style={{ color: '#f85149', fontSize: '0.85rem', marginTop: '0.75rem' }}>{featureError}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </div>
    );
};

// Create Club Modal
const CreateClubModal = ({ sa, onClose, onCreate }) => {
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [description, setDescription] = useState('');
    const [language, setLanguage] = useState('fi');
    const [saving, setSaving] = useState(false);

    const generateSlug = (text) => {
        return text
            .toLowerCase()
            .replace(/ä/g, 'a')
            .replace(/ö/g, 'o')
            .replace(/å/g, 'a')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const handleNameChange = (e) => {
        const newName = e.target.value;
        setName(newName);
        setSlug(generateSlug(newName));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name || !slug) return;
        
        setSaving(true);
        await onCreate({ name, slug, description, language });
        setSaving(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3><Plus size={20} /> {sa.clubs.createTitle}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group">
                            <label>{sa.clubs.clubName} *</label>
                            <input 
                                type="text" 
                                value={name} 
                                onChange={handleNameChange}
                                placeholder={sa.clubs.clubNamePlaceholder}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{sa.clubs.urlSlug} *</label>
                            <input 
                                type="text" 
                                value={slug} 
                                onChange={e => setSlug(e.target.value)}
                                placeholder={sa.clubs.urlSlugPlaceholder}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{sa.common.description}</label>
                            <textarea 
                                value={description} 
                                onChange={e => setDescription(e.target.value)}
                                placeholder={sa.clubs.descriptionPlaceholder}
                                rows={3}
                            />
                        </div>
                        <div className="form-group">
                            <label>{sa.common.language}</label>
                            <select value={language} onChange={e => setLanguage(e.target.value)}>
                                <option value="fi">{sa.clubs.languageFi}</option>
                                <option value="en">{sa.clubs.languageEn}</option>
                                <option value="sv">{sa.clubs.languageSv}</option>
                            </select>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            {sa.common.cancel}
                        </button>
                        <button type="submit" className="btn-primary" disabled={saving}>
                            {saving ? <Loader2 size={16} className="spin" /> : <Plus size={16} />}
                            {sa.clubs.createClub}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;

